import ApplicationController from './application_controller'
import Rails from '@rails/ujs'
import { useDispatch } from 'stimulus-use'
import { condensedNumber } from '@app/javascript/utils/number_utils.js'

const EMPTY_COUNTER_PLACEHOLDER = '-'

export default class extends ApplicationController {
  static targets = ['counter']
  static values = {
    searchParams: String,
    refreshUrl: String,
    refreshHttpMethod: String,
    refreshOnConnect: Boolean,
    handlesAsyncCounterTargets: Boolean
  }

  initialize() {
    useDispatch(this)
  }

  doConnect() {
    if (this.refreshOnConnectValue) {
      if (this.hasSearchParamsValue) {
        const searchParams = JSON.parse(this.searchParamsValue)
        this.retrieveCounts($.param(searchParams))
      } else {
        this.retrieveCounts()
      }
    }

    this.listenForCounterTargetsConnected()
  }

  reconnect(event) {
    this.connect()
    setTimeout(() => { this.dispatch('reconnect-complete') }, 0)
  }

  // Temp Solution while on Stimulus 2 and target callbacks not yet available.
  // TODO: When on Stimulus 3, remove this function and uncomment the +counterTargetConnected+ below.
  listenForCounterTargetsConnected() {
    if (!this.handlesAsyncCounterTargetsValue) return

    setTimeout(() => {
      if (this.currentCounts) {
        let countsUpdated
        this.counterTargets.forEach((elem) => {
          const isNewElem = elem.innerHTML.trim() === ''

          if (isNewElem) {
            this.showCountForElement(elem)
            countsUpdated = true
          }
        })

        if (countsUpdated) this.dispatch('countsUpdated')
      }

      this.listenForCounterTargetsConnected()
    }, 1000)
  }

  // TODO: Uncomment when on Stimulus 3. Target callbacks are not available on Stimulus 2
  // counterTargetConnected(elem) {
  //   // Because items can be fetched async, it can happen that the counts are
  //   // fetched before the corresponding list items containing the counter HTML element
  //   // are even in the DOM. So we listen whenever a new counter target is added
  //   // to show the corresponding count if available.
  //   if (this.handlesAsyncCounterTargetsValue) {
  //     this.showCountForElement(elem)
  //   }
  // }

  onSearchUpdated(event) {
    if (!event.detail) return

    const { params, triggeredBy } = event.detail

    if (triggeredBy === 'sortingChanged') return

    this.searchParamsValue = params
    this.retrieveCountsWithSearchParams()
  }

  retrieveCountsWithSearchParams() {
    if (this.hasSearchParamsValue) {
      this.retrieveCounts(this.searchParamsValue)
    }
  }

  retrieveCounts(serializedSearchParams = '') {
    this.removeElementsCount()

    this.dispatch('beforeRetrieveCounts')

    Rails.ajax({
      type: this.refreshHttpMethodValue || 'GET',
      url: this.refreshUrlValue,
      dataType: 'script',
      data: serializedSearchParams,
      success: (data) => {
        this.currentCounts = data.counts
        this.updateElementsCount()
        const eventData = {
          counts: data.counts,
          searchParams: new URLSearchParams(this.searchParamsValue)
        }
        this.dispatch('afterRetrieveCounts', eventData)
      }
    })
  }

  removeElementsCount() {
    this.currentCounts = null
    this.counterTargets.forEach((elem) => {
      elem.innerHTML = EMPTY_COUNTER_PLACEHOLDER
    })
  }

  updateElementsCount() {
    this.counterTargets.forEach((elem) => {
      this.showCountForElement(elem)
    })
  }

  showCountForElement(elem) {
    if (!this.currentCounts) return

    const elemNameRaw = this.getParam(elem, 'name')
    const elemNameClean = elemNameRaw.replace('PS-', '')
    let countName = elemNameRaw
    const groupName = this.getParam(elem, 'group-name')
    let count = 0
    // Handle special case for PS location filters
    if (groupName === 'locations' && this.refreshUrlValue === '/search/filter_counts') {
      if (elemNameRaw.indexOf('PS-') !== -1) {
        countName = elemNameClean + '_tech_services'
      } else {
        countName = elemNameClean + '_global'
      }
    }
    count += (this.currentCounts[groupName] || {})[countName] || 0
    if (groupName === 'locations') {
      count += (this.currentCounts.parent_locations || {})[countName] || 0
    }

    elem.innerHTML = condensedNumber(count || 0, { precision: 1 })
    elem.dataset.countNumber = count || 0
  }
}
